import { CheckCircle2 } from "lucide-react";
import Seo from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { EnquiryForm } from "@/components/EnquiryForm";
import { REFERRAL_BENEFITS } from "@/data/content";

export default function Referrals() {
  return (
    <>
      <Seo
        title="Referral partners — Brightside Befriending Services, Glasgow"
        siteName="Brightside Befriending Services"
        description="For social workers, GPs, discharge teams and care homes: a trusted private befriending option in Glasgow with PVG-checked companions and written visit updates."
      />
      <PageHero
        eyebrow="For professionals"
        lines={["A trusted pair of hands", "for your caseload."]}
        text="Social workers, GPs, discharge coordinators, care homes and assisted living managers: when someone needs company rather than care, we're the private option you can recommend with confidence."
      />

      <section className="px-4 pb-24 sm:px-6 lg:px-8" data-testid="referral-benefits">
        <div className="mx-auto grid max-w-7xl gap-6 sm:grid-cols-2">
          {REFERRAL_BENEFITS.map((b, i) => (
            <Reveal key={b.title} delay={i * 0.08}>
              <article className="flex h-full gap-4 rounded-3xl border border-sand bg-white p-8 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_20px_45px_rgba(28,56,41,0.1)]">
                <CheckCircle2 className="mt-1 h-5 w-5 shrink-0 text-terracotta" />
                <div>
                  <h2 className="font-serif text-lg text-forest sm:text-xl">{b.title}</h2>
                  <p className="mt-2 text-sm leading-relaxed text-pebble">{b.text}</p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="px-4 pb-24 sm:px-6 lg:px-8" data-testid="partnership-card-section">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <div className="relative overflow-hidden rounded-[2.5rem] bg-forest p-8 text-linen sm:p-12" data-testid="partnership-card">
              <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-terracotta/25 blur-3xl" />
              <div className="pointer-events-none absolute -bottom-28 -left-16 h-72 w-72 rounded-full bg-sage/25 blur-3xl" />
              <div className="relative grid items-center gap-10 lg:grid-cols-2">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.24em] text-terracotta-soft">For care settings</p>
                  <h2 className="mt-3 font-serif text-2xl sm:text-3xl">A concrete offer for managers</h2>
                  <p className="mt-4 text-sm leading-relaxed text-linen/70 sm:text-base">
                    One simple weekly arrangement: a PVG-checked befriender on-site for a half-day, every week — giving residents the one-to-one time busy rotas can't.
                  </p>
                  <ul className="mt-6 space-y-2.5">
                    {[
                      "3 hours on-site, same day and time each week",
                      "Up to 4 one-to-one resident visits per block",
                      "The same befriender every week",
                      "Written visit notes for your records",
                      "Simple monthly invoicing",
                    ].map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-sm text-linen/85">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-terracotta-soft" /> {f}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="rounded-3xl border border-linen/15 bg-forest-deep/60 p-8">
                  <p className="text-center text-xs font-semibold uppercase tracking-widest text-linen/50">Partnership options</p>
                  <div className="mt-5 space-y-3">
                    {[
                      { name: "Half-day each week", detail: "3 hours · up to 4 one-to-one visits" },
                      { name: "Full day each week", detail: "6 hours · up to 8 one-to-one visits" },
                      { name: "Multi-day cover", detail: "3+ days a week · tailored rota" },
                    ].map((t) => (
                      <div
                        key={t.name}
                        data-testid={`partnership-tier-${t.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        className="rounded-2xl border border-linen/10 bg-forest/40 px-5 py-4"
                      >
                        <p className="text-sm font-semibold text-linen">{t.name}</p>
                        <p className="text-xs text-linen/55">{t.detail}</p>
                      </div>
                    ))}
                  </div>
                  <p className="mt-5 text-center font-serif text-lg italic text-terracotta-soft" data-testid="partnership-pricing-note">
                    Clear, tailored pricing — quoted after a quick chat
                  </p>
                  <div className="text-center">
                    <a
                      href="#referral-form"
                      data-testid="partnership-cta-button"
                      className="mt-6 inline-block rounded-full bg-terracotta px-8 py-4 text-sm font-semibold text-linen shadow-[0_10px_26px_rgba(200,109,81,0.35)] transition-colors duration-300 hover:bg-terracotta-deep"
                    >
                      Arrange a partnership
                    </a>
                    <p className="mt-4 text-[11px] text-linen/40">Simple monthly invoicing · Assisted living, sheltered housing, care homes & agencies</p>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section id="referral-form" className="bg-white/50 px-4 py-24 sm:px-6 lg:px-8" data-testid="referral-form-section">
        <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-2">
          <div>
            <SectionHeading
              eyebrow="Make a referral"
              title="Tell us about your client or resident"
              text="Share the essentials and we'll call you back within one working day to talk through suitability, availability and next steps. With the family's consent, we can usually arrange a first visit within days."
            />
            <Reveal delay={0.2}>
              <ul className="mt-8 space-y-3 text-sm text-pebble">
                <li className="flex items-start gap-2.5"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-sage" /> Response within one working day, always by a person</li>
                <li className="flex items-start gap-2.5"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-sage" /> Written visit updates shared with referrers (with consent)</li>
                <li className="flex items-start gap-2.5"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-sage" /> Flexible block arrangements for care homes and agencies</li>
                <li className="flex items-start gap-2.5"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-sage" /> Assisted living & sheltered housing managers — arrange regular weekly visits for your residents with one simple agreement</li>
              </ul>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <div className="rounded-3xl border border-sand bg-white p-8 shadow-[0_16px_40px_rgba(28,56,41,0.06)] sm:p-10">
              <EnquiryForm kind="referral" showOrganisation buttonLabel="Send referral enquiry" testIdPrefix="referral" />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
